<script setup lang="ts">
import type { SizeVariant } from "~~/shared/types/store";

// `atLimit` — rozmiar już dodany do koszyka na maksymalną dostępną ilość.
// Wyznaczany w rodzicu (ProductCard) z danych koszyka; modal pozostaje prezentacyjny.
type PickerVariant = SizeVariant & { atLimit?: boolean };

const props = defineProps<{
  open: boolean;
  variants: PickerVariant[];
  productName?: string;
  pending?: boolean;
}>();

const emit = defineEmits<{
  close: [];
  select: [variant: SizeVariant];
}>();

function onSelect(variant: PickerVariant) {
  if (!variant.available || variant.atLimit || props.pending) return;
  emit("select", variant);
}

function handleKeydown(e: KeyboardEvent) {
  if (props.open && e.key === "Escape") emit("close");
}

watch(
  () => props.open,
  (open) => {
    if (!import.meta.client) return;
    document.body.style.overflow = open ? "hidden" : "";
  },
);

onMounted(() => document.addEventListener("keydown", handleKeydown));
onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
  if (import.meta.client) document.body.style.overflow = "";
});
</script>

<template>
  <Teleport to="body">
    <Transition name="size-picker-fade">
      <div
        v-if="open"
        class="size-picker-backdrop"
        @click.self="emit('close')"
      >
        <Transition name="size-picker-rise" appear>
          <div
            v-if="open"
            class="size-picker"
            role="dialog"
            aria-modal="true"
            aria-label="Wybierz rozmiar"
          >
            <button
              type="button"
              class="size-picker__close"
              aria-label="Zamknij"
              @click="emit('close')"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <p class="size-picker__title">Wybierz rozmiar</p>
            <p v-if="productName" class="size-picker__product">{{ productName }}</p>

            <div class="size-picker__options">
              <button
                v-for="v in variants"
                :key="v.id"
                type="button"
                class="size-picker__option"
                :class="{
                  'is-current': v.isCurrent,
                  'is-unavailable': !v.available,
                  'is-in-cart': v.available && v.atLimit,
                }"
                :disabled="!v.available || v.atLimit || pending"
                :title="
                  v.available && v.atLimit
                    ? 'Więcej nie mamy — to ostatnie sztuki'
                    : undefined
                "
                @click="onSelect(v)"
              >
                {{ v.rozmiar }}
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.size-picker-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 15, 15, 0.56);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 95;
}

.size-picker {
  position: relative;
  background: var(--color-white);
  width: min(420px, 100%);
  padding: 28px 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.size-picker__close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-white);
  border: 1px solid var(--color-border-md);
  border-radius: 50%;
  cursor: pointer;
  color: var(--color-black);
  transition:
    background var(--transition-fast),
    color var(--transition-fast),
    border-color var(--transition-fast);
}

.size-picker__close:hover {
  background: var(--color-black);
  color: var(--color-white);
  border-color: var(--color-black);
}

.size-picker__title {
  margin: 0;
  font-size: 1rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-black);
  line-height: 1.2;
}

.size-picker__product {
  margin: 0 0 12px;
  font-size: 0.8125rem;
  font-weight: var(--font-weight-light);
  color: var(--color-muted);
  line-height: 1.3;
  text-transform: uppercase;
}

.size-picker__options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.size-picker__option {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 52px;
  height: 46px;
  padding: 0 14px;
  background: var(--color-white);
  border: 1px solid var(--color-border-md);
  border-radius: 999px;
  font-family: var(--font-body);
  font-size: 0.9375rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  cursor: pointer;
  transition:
    background var(--transition-fast),
    color var(--transition-fast),
    border-color var(--transition-fast);
}

.size-picker__option:hover:not(.is-unavailable):not(:disabled) {
  background: var(--color-black);
  color: var(--color-white);
  border-color: var(--color-black);
}

.size-picker__option.is-current {
  border-color: var(--color-black);
  font-weight: var(--font-weight-normal);
}

.size-picker__option.is-unavailable {
  opacity: 0.32;
  cursor: not-allowed;
  text-decoration: line-through;
}

/* Rozmiar już w koszyku na maksymalną dostępną ilość — wyszarzony, ale bez
   przekreślenia (nie jest „wyprzedany", po prostu masz go już maksymalnie). */
.size-picker__option.is-in-cart {
  opacity: 0.4;
  cursor: not-allowed;
  background: var(--color-bg);
  color: var(--color-muted);
}

/* ── Transitions ── */
.size-picker-fade-enter-from,
.size-picker-fade-leave-to {
  opacity: 0;
}

.size-picker-fade-enter-active {
  transition: opacity 200ms ease;
}

.size-picker-fade-leave-active {
  transition: opacity 160ms ease;
}

.size-picker-rise-enter-from {
  opacity: 0;
  transform: translateY(14px) scale(0.98);
}

.size-picker-rise-enter-active {
  transition:
    opacity 240ms ease,
    transform 240ms cubic-bezier(0.16, 1, 0.3, 1);
}

@media (max-width: 560px) {
  .size-picker-backdrop {
    align-items: flex-end;
    padding: 0;
  }

  .size-picker {
    width: 100%;
    padding: 24px 20px calc(24px + env(safe-area-inset-bottom));
  }

  .size-picker-rise-enter-from {
    transform: translateY(40px) scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .size-picker-fade-enter-active,
  .size-picker-fade-leave-active,
  .size-picker-rise-enter-active {
    transition: none;
  }

  .size-picker-rise-enter-from {
    transform: none;
  }
}
</style>
